import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { LockedOverlay } from "../components/LockedOverlay";
import { PlayCircle } from "lucide-react";

export default function VideoLibrary() {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);
  const isPaid = user?.subscription_status === "paid" || user?.role === "admin";

  useEffect(() => {
    api.get("/videos").then(({ data }) => setVideos(data)).catch(() => {});
  }, []);

  return (
    <div className="container-page py-10" data-testid="video-library-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink">Video library</h1>
      <p className="mt-2 text-muted2">Short, focused video lessons from nutrition educators.</p>

      {videos.length === 0 ? (
        <div className="mt-12 text-center text-muted2 bg-cardm border border-subtle rounded-2xl p-12">
          No videos yet. Check back soon!
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((v) => {
            const unlocked = v.is_free_preview || isPaid;
            return (
              <div key={v.id} className="bg-white border border-subtle rounded-2xl overflow-hidden shadow-ambient hover:shadow-hover transition-all" data-testid={`video-card-${v.id}`}>
                <div className="relative aspect-video bg-cardm">
                  {v.thumbnail_url ? (
                    <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover" />
                  ) : (
                    <img src={`https://img.youtube.com/vi/${v.youtube_id || "dQw4w9WgXcQ"}/hqdefault.jpg`} alt="" className="w-full h-full object-cover" onError={(e)=>{e.target.style.display='none';}} />
                  )}
                  {unlocked && v.youtube_id && (
                    <button onClick={() => setActive(v)} data-testid={`play-${v.id}`}
                      className="absolute inset-0 grid place-items-center bg-black/30 hover:bg-black/40 transition-all">
                      <PlayCircle size={56} className="text-white drop-shadow-lg" />
                    </button>
                  )}
                  {!unlocked && <LockedOverlay message="Members-only video" />}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2">
                    {v.is_free_preview && <span className="text-xs font-semibold uppercase tracking-wider text-leaf bg-leaf/10 rounded-full px-2 py-0.5">Free preview</span>}
                  </div>
                  <h3 className="mt-2 font-heading font-semibold text-ink">{v.title}</h3>
                  <p className="mt-1 text-sm text-muted2 line-clamp-2">{v.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Player modal */}
      {active && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={() => setActive(null)} data-testid="video-player-modal">
          <div className="w-full max-w-4xl bg-white rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative aspect-video bg-black">
              <iframe
                title={active.title}
                src={`https://www.youtube.com/embed/${active.youtube_id}?autoplay=1`}
                className="absolute inset-0 w-full h-full"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </div>
            <div className="p-5">
              <h3 className="font-heading font-semibold text-ink">{active.title}</h3>
              <p className="mt-1 text-sm text-muted2">{active.description}</p>
              <button onClick={() => setActive(null)} className="mt-4 text-sm text-moss font-medium hover:underline" data-testid="close-video-modal">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
